import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdvertiser, ALL_CLIENTS } from '../contexts/AdvertiserContext';
import { useAuth } from '../contexts/AuthContext';
import { getCurrencySymbol } from '../utils/currency';
import '../styles/client-balance.css';

const AdvertiserSwitcher: React.FC = () => {
  const { t } = useTranslation();
  const { advertisers, selectedAdvertiser, isAllClients, setSelectedAdvertiserId, isLoading } = useAdvertiser();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showBalance, setShowBalance] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
        setShowBalance(false);
      }
    };
    if (isOpen || showBalance) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, showBalance]);

  useEffect(() => {
    if (isOpen && searchRef.current) setTimeout(() => searchRef.current?.focus(), 80);
  }, [isOpen]);

  if (isLoading || advertisers.length === 0) return null;
  if (advertisers.length === 1 && user?.role === 'client') return null;

  // Search runs over ALL clients. Without a search term, a SHORT list shows everyone;
  // only a LONG list is trimmed to the "quick list" — clients with media/research spend in
  // the last 2 months or created in the last 30 days — plus the selected client. Trimming a
  // short list used to hide an idle client (no recent spend) with no search box to reach it,
  // so an agency's inactive client became invisible.
  const QUICKLIST_MIN = 12;
  const filtered = search
    ? advertisers.filter(a =>
        a.display_name.toLowerCase().includes(search.toLowerCase()) ||
        a.advertiser_id.includes(search)
      )
    : advertisers.length <= QUICKLIST_MIN
      ? advertisers
      : advertisers.filter(a =>
          a.in_quicklist ||
          (!isAllClients && a.advertiser_id === selectedAdvertiser.advertiser_id)
        );

  const displayName = isAllClients ? t('switcher.allClients') : selectedAdvertiser.display_name;

  // Balance
  const hasBalance = !isAllClients && selectedAdvertiser.balance !== undefined;
  const balance = selectedAdvertiser.balance || 0;
  const balCurrency = selectedAdvertiser.billing_currency || 'EUR';
  const balSymbol = getCurrencySymbol(balCurrency);
  const balNegative = balance < 0;
  const balAbs = Math.abs(balance);
  const [balWhole, balCents] = balAbs.toFixed(2).split('.');
  const balFormatted = balWhole.replace(/\B(?=(\d{3})+(?!\d))/g, '\u2009');

  return (
    <nav className="nav-section client-section" ref={containerRef}>
      <div className="nav-label">{t('switcher.client')}</div>
      <button
        className={`nav-item client-nav-item ${isOpen ? 'active' : ''}`}
        onClick={() => { setIsOpen(!isOpen); setSearch(''); setShowBalance(false); }}
      >
        <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
        </svg>
        <span style={{flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
          {displayName}
        </span>
        {hasBalance && (
          <span
            className={`client-balance-inline ${showBalance ? 'is-active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setShowBalance(!showBalance); setIsOpen(false); }}
            title={t('switcher.showBalance')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 18v1a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v1"/>
              <path d="M21 8H12a2 2 0 00-2 2v4a2 2 0 002 2h9"/>
              <circle cx="16" cy="12" r="1"/>
            </svg>
          </span>
        )}
        {advertisers.length > 1 && !hasBalance && (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none'}}>
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {advertisers.length > 1 && hasBalance && (
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{flexShrink: 0, transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'none'}}>
            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      {/* Balance tooltip */}
      {showBalance && hasBalance && (
        <div className="client-balance-tooltip">
          <div className="client-balance-tooltip-label">{t('switcher.balance')}</div>
          <div className={`client-balance-tooltip-amount ${balNegative ? 'negative' : balance > 0 ? 'positive' : ''}`}>
            {balNegative && <span>&minus;</span>}
            <span className="client-balance-tooltip-symbol">{balSymbol}</span>
            {balFormatted}
            <span className="client-balance-tooltip-cents">.{balCents}</span>
          </div>
        </div>
      )}

      {isOpen && (
        <div className="client-dropdown">
          {advertisers.length > 1 && (
            <div className="client-dropdown-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder={t('switcher.searchClients')}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          )}
          <div className="client-dropdown-list">
            {!search && (
              <button
                className={`client-dropdown-item ${isAllClients ? 'is-active' : ''}`}
                onClick={() => {
                  setSelectedAdvertiserId(ALL_CLIENTS.advertiser_id);
                  setIsOpen(false);
                  setSearch('');
                }}
              >
                <span className="client-dropdown-item-name">{t('switcher.allClients')}</span>
                {isAllClients && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </button>
            )}
            {!search && <div className="client-dropdown-divider" />}
            {filtered.map(a => {
              const isActive = a.advertiser_id === selectedAdvertiser.advertiser_id && !isAllClients;
              return (
                <button
                  key={a.advertiser_id}
                  className={`client-dropdown-item ${isActive ? 'is-active' : ''}`}
                  onClick={() => {
                    setSelectedAdvertiserId(a.advertiser_id);
                    setIsOpen(false);
                    setSearch('');
                  }}
                >
                  <span className="client-dropdown-item-name">{a.display_name}</span>
                  {isActive && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              );
            })}
            {search && filtered.length === 0 && (
              <div className="client-dropdown-empty">{t('switcher.nothingFound')}</div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default AdvertiserSwitcher;
